import React from 'react';
import PropTypes from 'prop-types';

import Flickity from 'react-flickity-component';
import 'flickity/dist/flickity.css';

const EstateTeaserLink = ({ estate, children }) => {
    if (estate.allowDetailPage) {
        return (
            <a className="estate-teaser__link" href={estate.url}>{children}</a>
        );
    }

    return children;
};

const EstateFavorite = ({ estate }) => {
    const enableFavorites = window.enableFavorites ? window.enableFavorites : false;

    const expiryDate = new Date();
    expiryDate.setMonth(expiryDate.getMonth() + 12);

    if (enableFavorites && estate.allowDetailPage) {
        const className = checkFavorites(estate.id);

        return (
            <button className={className} data-estate-id={estate.id}
                    onClick={(e) => toggleFavorite(e, estate.id)}>
                <svg className="is-favorite" viewBox="0 0 20 20">
                    <use xlinkHref="#svg-heart" />
                </svg>
                <svg className="no-favorite" viewBox="0 0 20 20">
                    <use xlinkHref="#svg-heart-outline" />
                </svg>
            </button>
        );
    } else {
        return false;
    }

    function checkFavorites(id) {
        if (!window.favorites.includes(id)) {
            return 'estate-favorite';
        } else {
            return 'estate-favorite is-active';
        }
    }

    function toggleFavorite(e, id) {
        e.currentTarget.classList.toggle('is-active');

        if (!window.favorites.includes(id)) {
            window.favorites.push(id);

            document.cookie = "favorites=" + window.favorites.toString() + ";path=/;expires=" + expiryDate.toUTCString();
        } else {
            const index = window.favorites.indexOf(id);

            if (index > -1) {
                window.favorites.splice(index, 1);
            }

            if (window.favorites.length === 0) {
                document.cookie = "favorites=;";
            } else {
                document.cookie = "favorites=" + window.favorites.toString() + ";path=/;expires=" + expiryDate.toUTCString();
            }
        }
    }
}

class EstateTeaser extends React.Component {
    componentDidMount = () => {
        if (this.props.estate.allowDetailPage) {
            // this loads all the images when they click the arrows
            this.flickity.on('change', () => {
                this.flickity.cells.forEach((cell) => {
                    cell.element.style.backgroundImage = `url('${cell.element.dataset.image}')`;
                });
            });

            // this is for the initial image.
            this.flickity.on('select', () => {
                const { selectedElement } = this.flickity;

                if (selectedElement) {
                    selectedElement.style.backgroundImage = `url('${selectedElement.dataset.image}')`;
                }
            });

            let offset = 1;
            const update = () => {
                this.flickity.resize();

                if (offset < 5) {
                    setTimeout(() => {
                        update();
                    }, 500 * offset);
                }

                offset += 1;
            };

            setTimeout(() => {
                update();
            }, 500);
        }
    };

    // Depeding on whether the estate is available or not we render Flickity.
    renderImage = () => {
        if (this.props.estate.allowDetailPage) {
            return (
                <Flickity
                    className="estate-teaser-image-carousel"
                    options={{
                        pageDots: false,
                        wrapAround: true,
                    }}
                    flickityRef={c => this.flickity = c}
                >
                    {this.props.estate.images.slice(0, window.estateTeaserMaxPreviewImages).map((image) => {
                        if (this.props.estate.allowDetailPage) {
                            return (
                                <a
                                    className="estate-teaser-image-carousel__item"
                                    href={this.props.estate.url}
                                    aria-hidden="true"
                                    data-image={image}
                                    key={image}
                                />
                            );
                        }

                        return (
                            <div
                                className="estate-teaser-image-carousel__item"
                                data-image={image}
                                key={image}
                            />
                        );
                    })}
                </Flickity>
            );
        } else {
            return (
                <div
                    className="estate-teaser-image-carousel__item"
                    style={{ backgroundImage: `url(${this.props.estate.images[0]})` }}
                />
            );
        }
    };

    render = () => {
        const { estate } = this.props;

        const onMouseEnter = () => {
            if (window.setActiveHitId) {
                window.setActiveHitId(estate.id);
            }
        };

        return (
            <div className="estate-teaser" onMouseEnter={onMouseEnter}>
                <div className="estate-teaser__purpose font-supplementary--bold">
                    {estate.statusLabel}
                </div>
                { estate.isNew && (
                    <div className="estate-teaser__new font-supplementary--bold">
                        Nieuw
                    </div>
                )}
                { estate.extraLabel && (
                    <div className="estate-teaser__extra font-supplementary--bold">
                        {estate.extraLabel}
                    </div>
                )}
                <div className="estate-teaser__images-wrapper">
                    { this.renderImage() }
                </div>

                <EstateFavorite estate={estate}></EstateFavorite>

                <EstateTeaserLink estate={estate}>
                    <div className="estate-teaser__metadata">
                        <div className="estate-teaser__statistics">
                            {estate.bedrooms > 0 && (
                                <div className="estate-teaser__statistic">
                                    <img
                                        src="https://img.icons8.com/windows/44/000000/bed.png"
                                        alt="slaapkamers"
                                    />
                                    {estate.bedrooms}
                                </div>
                            )}
                            {estate.bathrooms > 0 && (
                                <div className="estate-teaser__statistic">
                                    <img
                                        src="https://img.icons8.com/windows/44/000000/bath.png"
                                        alt="badkamers"
                                    />
                                    {estate.bathrooms}
                                </div>
                            )}
                            {estate.area > 0 && (
                                <div className="estate-teaser__statistic">
                                    <img
                                        src="https://img.icons8.com/windows/44/000000/floor-plan.png"
                                        alt="oppervlakte"
                                    />
                                    {estate.area} m²
                                </div>
                            )}
                        </div>
                        {Boolean(estate.displayPrice) && estate.allowDetailPage && (
                            <div className="estate-teaser__price font-supplementary--bold">
                                {estate.price}
                            </div>
                        )}
                        {estate.allowDetailPage && Boolean(estate.displayPrice) === false && (
                            <div className="estate-teaser__price font-supplementary--bold">
                                {window.priceFalse}
                            </div>
                        )}
                    </div>
                    <div className="estate-teaser__content">
                        <div className="estate-teaser__title font-supplementary--bold">
                            {estate.category}
                            <small className="font-supplementary">{estate.location}</small>
                        </div>
                    </div>
                </EstateTeaserLink>
            </div>
        );
    };
}

EstateTeaser.propTypes = {
    estate: PropTypes.shape({
        allowDetailPage: PropTypes.bool,
        area: PropTypes.number,
        bedrooms: PropTypes.number,
        bathrooms: PropTypes.number,
        displayCity: PropTypes.string,
        displayPrice: PropTypes.bool,
        id: PropTypes.string,
        image: PropTypes.string,
        isProject: PropTypes.bool,
        price: PropTypes.number,
        purpose: PropTypes.string,
        url: PropTypes.string.isRequired,
        subcategory: PropTypes.string,
        surface: PropTypes.number,
        title: PropTypes.string.isRequired,
        reference: PropTypes.string,
        extraLabel: PropTypes.string,
    }).isRequired,
};

export { EstateTeaser };
